import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface Props {
  orgSlug: string;
  projectSlug: string;
  onCreated: (id: string) => void;
  /** Called when same-project dup → existing wizard should be opened. */
  onResumeExisting: (id: string) => void;
}

interface CreateResponse {
  id: string;
  hostname: string;
}

/**
 * Step 1 — paste hostname. Normalisation preview is a best-effort echo
 * (lowercase + trailing dot strip). Server-side normalisation is the
 * authority; we don't try to mirror IDN/punycode here.
 */
export function EnterHost({ orgSlug, projectSlug, onCreated, onResumeExisting }: Props) {
  const qc = useQueryClient();
  const [raw, setRaw] = useState("");

  const preview = useMemo(() => {
    return raw.trim().toLowerCase().replace(/\.$/, "").replace(/^https?:\/\//, "");
  }, [raw]);

  const create = useMutation<CreateResponse, ApiError>({
    mutationFn: () =>
      api.post<CreateResponse>(`/orgs/${orgSlug}/projects/${projectSlug}/domains`, {
        hostname: raw,
      }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["project-domains", orgSlug, projectSlug] });
      onCreated(r.id);
    },
    onError: (err) => {
      if (err.code === "already_added_to_project") {
        const id = (err.details as { existingId?: string } | null | undefined)?.existingId;
        if (typeof id === "string") onResumeExisting(id);
      }
    },
  });

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label htmlFor="custom-host" className="text-xs">Hostname</Label>
        <Input
          id="custom-host"
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          placeholder="app.example.com  (or example.com)"
          className="font-mono text-sm"
          onKeyDown={(e) => {
            if (e.key === "Enter" && raw.trim()) create.mutate();
          }}
        />
        {preview && preview !== raw.trim() && (
          <div className="text-2xs text-subtle-foreground">
            Will save as <span className="font-mono">{preview}</span>
          </div>
        )}
      </div>
      {create.error && (
        <div className="text-xs text-danger">{create.error.message}</div>
      )}
      <div className="flex justify-end">
        <Button
          variant="primary"
          size="sm"
          disabled={!raw.trim() || create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Continue
        </Button>
      </div>
    </div>
  );
}
